import { useState, useEffect, useRef } from "react";
import type { GalleryPreset, PageBlueprintItem } from "@profolio/templates";
import { resolvePresetComposition } from "@profolio/templates";
import { loadTemplate, getPalette, getFontPairing } from "@profolio/templates/browser";
import {
  Monitor,
  Tablet,
  Smartphone,
  X,
  Sparkles,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";

interface TemplatePreviewModalProps {
  preset: GalleryPreset | null;
  isOpen: boolean;
  onClose: () => void;
  onApply: (preset: GalleryPreset) => void;
  userTier?: "starter" | "pro" | "premium";
}

type Viewport = "desktop" | "tablet" | "mobile";

export function TemplatePreviewModal({
  preset,
  isOpen,
  onClose,
  onApply,
  userTier = "starter",
}: TemplatePreviewModalProps) {
  const [viewport, setViewport] = useState<Viewport>("desktop");
  const [activeSlug, setActiveSlug] = useState<string>("");
  const [html, setHtml] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!isOpen || !preset) {
      setHtml("");
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    async function generatePreview() {
      if (!preset) return;
      const composition = resolvePresetComposition(preset.presetId);
      if (!composition) {
        setIsLoading(false);
        return;
      }

      const { structure, theme, blueprint, demoDataset } = composition;

      try {
        // Resolve tier projection (uses user's tier, or preset's tier if higher)
        const effectiveTier = userTier === "premium" ? "premium" : userTier === "pro" ? "pro" : preset.requiredTier;
        const tierAdaptation = blueprint.adaptations[effectiveTier] || blueprint.adaptations.starter;

        const visiblePages: PageBlueprintItem[] = tierAdaptation.pages;
        const currentPage =
          visiblePages.find((p) => (activeSlug ? p.slug === activeSlug : p.isHome)) ||
          visiblePages.find((p) => p.isHome) ||
          visiblePages[0];

        const templateFn = await loadTemplate(structure.themeKey || "classic-academic");
        const palette = getPalette(theme.colorPalette);
        const fonts = getFontPairing(theme.fontPairing);

        // Build navigation items
        const navigation = visiblePages.map((p, idx) => ({
          id: `p-${p.slug || "home"}`,
          slug: p.slug,
          label: p.navigationLabel || p.title,
          isHome: p.isHome,
          sortOrder: idx,
        }));

        // Filter demo sections by page assignment (or show on Home)
        const renderedSections = (demoDataset.sections || []).filter((s) => {
          if (currentPage.isHome) {
            return currentPage.defaultSectionOrder.includes(s.type);
          }
          return currentPage.defaultSectionOrder.includes(s.type);
        });

        const profileData = {
          ...demoDataset.profile,
          themeKey: structure.themeKey,
          colorPalette: theme.colorPalette,
          fontPairing: theme.fontPairing,
          styleRecipe: theme.styleRecipe,
          backgroundStyle: theme.backgroundStyle,
          animationLevel: theme.animationLevel,
          layoutDensity: theme.layoutDensity,
          cornerRadius: theme.cornerRadius,
          typographyScale: theme.typographyScale,
          sectionArrangement: structure.layoutModel,
          layoutOverrides: {
            heroLayout: structure.heroLayout,
            ...(theme.layoutOverrides || {}),
            ...(structure.heroOverrides || {}),
            hero: {
              heroLayout: structure.heroLayout,
              ...((theme.layoutOverrides as any)?.hero || {}),
              ...(structure.heroOverrides || {}),
            },
          },
          navigation,
          activeSlug: currentPage.slug,
          currentPageTitle: currentPage.title,
          tier: effectiveTier,
        };

        const generatedHtml = templateFn({
          profile: profileData,
          sections: renderedSections,
          palette,
          fonts,
          mode: "preview",
        });

        if (isMounted) {
          // Prepend noindex and disable external interactions
          const safeHtml = `
            <!DOCTYPE html>
            <html>
              <head>
                <meta name="robots" content="noindex, nofollow" />
                <style>
                  /* Demonstration preview watermarks & protection */
                  a { pointer-events: none !important; }
                  form, input, button:not([data-nav-button]) { pointer-events: none !important; }
                </style>
              </head>
              <body>${generatedHtml}</body>
            </html>
          `;
          setHtml(safeHtml);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Preview render failed:", err);
        if (isMounted) setIsLoading(false);
      }
    }

    generatePreview();

    return () => {
      isMounted = false;
    };
  }, [isOpen, preset, activeSlug, userTier]);

  if (!isOpen || !preset) return null;

  const composition = resolvePresetComposition(preset.presetId);
  const effectiveTier = userTier === "premium" ? "premium" : userTier === "pro" ? "pro" : preset.requiredTier;
  const pagesList: PageBlueprintItem[] = composition?.blueprint.adaptations[effectiveTier]?.pages || [];

  const getViewportWidth = () => {
    switch (viewport) {
      case "mobile":
        return "w-[375px]";
      case "tablet":
        return "w-[768px]";
      case "desktop":
      default:
        return "w-full max-w-[1200px]";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-md animate-in fade-in-50 duration-200">
      {/* ── Top Header Toolbar ── */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-card shrink-0 select-none">
        {/* Left: Template info */}
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-foreground">{preset.name}</h2>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 uppercase">
                {preset.category.replace(/-/g, " ")}
              </span>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground uppercase">
                {preset.requiredTier}
              </span>
            </div>
            <p className="text-xs text-muted-foreground truncate max-w-md">
              {preset.tagline}
            </p>
          </div>
        </div>

        {/* Center: Device Viewport Switcher */}
        <div className="flex items-center gap-1 bg-muted/60 p-1 rounded-xl border border-border/80">
          <button
            type="button"
            onClick={() => setViewport("desktop")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
              viewport === "desktop"
                ? "bg-card text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Monitor className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Desktop</span>
          </button>

          <button
            type="button"
            onClick={() => setViewport("tablet")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
              viewport === "tablet"
                ? "bg-card text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Tablet className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Tablet</span>
          </button>

          <button
            type="button"
            onClick={() => setViewport("mobile")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
              viewport === "mobile"
                ? "bg-card text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Smartphone className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Mobile</span>
          </button>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-3">
          <Button
            type="button"
            size="sm"
            onClick={() => onApply(preset)}
            className="text-xs font-semibold shadow-xs cursor-pointer"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Use This Template
          </Button>

          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ── Subheader: Multi-page navigation simulator ── */}
      {pagesList.length > 1 && (
        <div className="flex items-center justify-between px-6 py-2 border-b border-border/60 bg-muted/30 text-xs shrink-0">
          <div className="flex items-center gap-2 text-muted-foreground font-medium">
            <Layers className="h-3.5 w-3.5 text-primary" />
            <span>Multi-Page Navigation:</span>
          </div>

          <div className="flex items-center gap-1.5">
            {pagesList.map((page) => {
              const isActive = activeSlug === page.slug || (!activeSlug && page.isHome);
              return (
                <button
                  key={page.slug}
                  type="button"
                  onClick={() => setActiveSlug(page.slug)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer ${
                    isActive
                      ? "bg-primary text-primary-foreground font-semibold"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}
                >
                  {page.title}
                </button>
              );
            })}
          </div>

          <div className="text-[11px] text-muted-foreground hidden md:block">
            Demonstration Mode &bull; Production Renderer Pipeline
          </div>
        </div>
      )}

      {/* ── Main Preview Iframe Canvas ── */}
      <div className="flex-1 overflow-auto bg-muted/40 p-4 sm:p-6 flex items-start justify-center">
        <div
          className={`h-full min-h-[500px] transition-[width] duration-300 rounded-2xl overflow-hidden border border-border shadow-2xl bg-white ${getViewportWidth()}`}
        >
          {isLoading ? (
            <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
              Compiling real production template preview...
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              srcDoc={html}
              title={`${preset.name} Live Preview`}
              className="h-full w-full border-none"
              sandbox="allow-same-origin allow-scripts"
            />
          )}
        </div>
      </div>
    </div>
  );
}
